import React, { cloneElement, useCallback } from 'react';
import type { FunctionComponent, ReactElement } from 'react';

import {
  BINARY_DECOMPOSITION,
  SNIPPET_DETECTION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../shared/featureFlags/flagNames';
import Locator from '../../../../shared/Locator';
import { useLocation, useParams } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { getProjectBaseURLFromParams } from '../../../utils';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import type { RevisionRouteParams } from '../projectDashboardTypes';

type InventoryPath = 'dependencies' | 'snippets' | 'binary' | 'vendored';

/**
 * ProjectInventoryBrowseContainer - When the Inventory UI FF is enabled, this will display the sidebar nav for
 * both the Dependencies, Snippets, and Binary Analysis (now grouped together as "Inventory").
 */
const ProjectInventoryBrowseContainer: FunctionComponent = React.memo(({ children, ...props }) => {
  const params = useParams<RevisionRouteParams>();
  const location = useLocation();

  const baseProjectURL = getProjectBaseURLFromParams(params);
  const isPathActive = useCallback((pathPart: InventoryPath) => {
    const pathWithoutTrailingSlash = location.pathname.replace(/\/$/, '');
    return pathWithoutTrailingSlash.endsWith(pathPart);
  }, [location.pathname]);

  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  const isBinaryProject = Locator.parse(params.project_id).fetcher === 'binary';

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavHeading>Inventory</SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem
          to={`${baseProjectURL}/browse/dependencies`}
          isActive={isPathActive('dependencies')}
        >
          Dependencies
        </SidebarNavLayout.NavItem>
        {isSnippetScanningEnabled && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/browse/snippets`}
            isActive={isPathActive('snippets')}
            >
            Snippets
          </SidebarNavLayout.NavItem>
        )}
        {isBinaryDecompositionEnabled && isBinaryProject && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/browse/binary`}
            isActive={isPathActive('binary')}
            >
            Binary Analysis
          </SidebarNavLayout.NavItem>
        )}
        {isVendoredDependenciesEnabled && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/browse/vendored`}
            isActive={isPathActive('vendored')}
            >
            Vendored
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        {cloneElement(children as ReactElement, { ...props, scope: 'project' })}
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
});

export default ProjectInventoryBrowseContainer;
